import cx from 'classnames';

import DiscordIcon from '~/components/Icons/DiscordIcon';
import ThreadsIcon from '~/components/Icons/ThreadsIcon';
import type { Publication } from '~/interfaces/publication';
import makeExternalUrl from '~/utils/makeExternalUrl';

interface SocialIconsProps {
  publication: Publication;
  size: 'sm' | 'md';
}

const SocialIcons = ({ publication, size }: SocialIconsProps) => {
  return (
    <div className="flex items-end space-x-2">
      {publication.twitter_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.twitter_url)} aria-label="X">
          <svg
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <title>X</title>
            <g>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </g>
          </svg>
        </a>
      )}
      {publication.linkedin_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.linkedin_url)} aria-label="LinkedIn">
          <svg
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
          >
            <title>LinkedIn</title>
            <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" />
          </svg>
        </a>
      )}
      {publication.youtube_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.youtube_url)} aria-label="Youtube">
          <svg
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <title>YouTube</title>
            <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
          </svg>
        </a>
      )}
      {publication.tiktok_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.tiktok_url)} aria-label="Tiktok">
          <svg
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 2859 3333"
            shapeRendering="geometricPrecision"
            textRendering="geometricPrecision"
            imageRendering="optimizeQuality"
            fillRule="evenodd"
            clipRule="evenodd"
          >
            <title>Tiktok</title>
            <path d="M2081 0c55 473 319 755 778 785v532c-266 26-499-61-770-225v995c0 1264-1378 1659-1932 753-356-583-138-1606 1004-1647v561c-87 14-180 36-265 65-254 86-398 247-358 531 77 544 1075 705 992-358V1h551z" />
          </svg>
        </a>
      )}
      {publication.facebook_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.facebook_url)} aria-label="Facebook">
          <svg
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <title>Facebook</title>
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        </a>
      )}
      {publication.instagram_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.instagram_url)} aria-label="Instagram">
          <svg
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <title>Instagram</title>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </a>
      )}
      {publication.threads_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.threads_url)} aria-label="Threads">
          <ThreadsIcon
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
          />
        </a>
      )}
      {publication.discord_url && (
        <a target="_blank" rel="noreferrer" href={makeExternalUrl(publication.discord_url)} aria-label="Discord">
          <DiscordIcon
            className={cx(
              'fill-current opacity-40 hover:opacity-100',
              size === 'sm' ? 'h-4 w-4' : '',
              size === 'md' ? 'h-6 w-6' : '',
            )}
          />
        </a>
      )}
    </div>
  );
};

export default SocialIcons;
